import React, { useState } from "react";
import { Search, MessageSquare, Bookmark } from "lucide-react";
import { usePermission } from "../../hooks/usePermission";
import { PERMISSIONS } from "../../config/permissions";
import Avatar from "../common/Avatar";
import ImageOrInitials from "../agency/ImageOrInitials";
import ClubVerifiedBadge from "../agent-tender/ClubVerifiedBadge";
import AgentTenderPreviewModal from "../agent-tender/AgentTenderPreviewModal";
import { buildFinRows } from "../../utils/agentRequestFigures";
import {
  formatRequestTitle,
  formatTypeOfTender,
  formatLeagueTier,
  formatTransferWindow,
} from "../../constants/agentTenderChoices";
import { formatRange } from "../../utils/tenderFormatters";
import { formatAbsoluteDate } from "../../utils/searchRequestFormat";

// Compact LIST-view row for the N2 club browse (C13), the row counterpart of
// ClubRequestCard. ANONYMISED exactly like the card: only the agency + agent are
// shown, never the club behind the request (no club name / logo / division) —
// the same product invariant, just a denser layout. Same actions: save, quick
// preview, and message-agent (or the contacted marker once a chat exists).
const AgentRequestListRow = ({ request, onMessage, onOpenChat, onToggleSave }) => {
  const {
    is_saved,
    type_of_tender,
    league_tier,
    country,
    transfer_window,
    verification_status,
    posted_agency,
    responsible_agent,
    min_age,
    max_age,
    you_contacted,
    contacted_by,
    contacted_at,
  } = request;

  const [showPreview, setShowPreview] = useState(false);
  // Same contact_agents gate as ClubRequestCard — hidden without the permission.
  const canContactAgents = usePermission(PERMISSIONS.CONTACT_AGENTS);
  const windowInfo = formatTransferWindow(transfer_window);
  const ageLimit = formatRange(min_age, max_age, "years");
  const meta = [
    type_of_tender ? formatTypeOfTender(type_of_tender) : null,
    country?.name || null,
    league_tier ? formatLeagueTier(league_tier) : null,
    ageLimit ? `Age ${ageLimit}` : null,
    windowInfo ? windowInfo.label || windowInfo.dates : null,
    // V2: real figures replace the budget band on every request surface.
    ...buildFinRows(request, "mo").map((r) => `${r.label} ${r.value}`),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg px-4 py-3 flex items-center gap-4">
      {/* Spec — position/role + coarse anonymised meta */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[15px] font-semibold text-[#0B0B0B] capitalize truncate">
            {formatRequestTitle(request)}
          </span>
          <ClubVerifiedBadge status={verification_status} />
        </div>
        {meta && <span className="text-[12px] text-gray-500">{meta}</span>}
      </div>

      {/* Sender — agency + agent (never the club) */}
      <div className="hidden sm:flex items-center gap-2 w-52 min-w-0">
        {posted_agency?.logo_url ? (
          <img
            src={posted_agency.logo_url}
            alt={posted_agency?.name || "Agency"}
            className="w-7 h-7 object-contain rounded flex-none"
            referrerPolicy="no-referrer"
          />
        ) : (
          <ImageOrInitials src={null} alt={posted_agency?.name} size="small" />
        )}
        <div className="min-w-0">
          <div className="text-[12.5px] font-medium text-[#242426] truncate">
            {posted_agency?.name}
          </div>
          {responsible_agent && (
            <div className="flex items-center gap-1.5">
              <Avatar
                src={responsible_agent.avatar}
                fallbackText={responsible_agent.display_name}
                size={14}
              />
              <span className="text-[11px] text-[#535370] truncate">
                {responsible_agent.display_name}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-none">
        {onToggleSave && (
          <button
            type="button"
            onClick={() => onToggleSave(request)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            aria-label={is_saved ? "Unsave request" : "Save request"}
            aria-pressed={!!is_saved}
            title={is_saved ? "Saved" : "Save"}
          >
            <Bookmark
              className={`w-4 h-4 ${
                is_saved ? "text-[#2283FF] fill-[#2283FF]" : "text-[#535370]"
              }`}
            />
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowPreview(true)}
          className="p-2 hover:bg-gray-100 rounded-lg"
          aria-label="Preview request"
          title="Quick preview"
        >
          <Search className="w-4 h-4 text-[#535370]" />
        </button>
        {you_contacted ? (
          <button
            type="button"
            onClick={() => onOpenChat && onOpenChat(request)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E8F1FF] text-[12px] font-semibold text-[#1B6FE0] whitespace-nowrap"
            title={
              contacted_by?.name
                ? `Contacted by ${contacted_by.name}${
                    contacted_at ? ` · ${formatAbsoluteDate(contacted_at)}` : ""
                  }`
                : "Contacted"
            }
          >
            <MessageSquare className="w-4 h-4" />
            Open chat
          </button>
        ) : (
          canContactAgents &&
          onMessage && (
            <button
              type="button"
              onClick={() => onMessage(request)}
              className="px-3 py-2 rounded-lg bg-[#2283FF] text-white text-[12px] font-semibold hover:bg-blue-600 whitespace-nowrap"
            >
              Message
            </button>
          )
        )}
      </div>

      <AgentTenderPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        tender={request}
        variant="feed"
        isOwn={false}
      />
    </div>
  );
};

export default AgentRequestListRow;
